import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from 'next-themes';
import { useAuth } from '../contexts/AuthContext';
import { useContent } from '../contexts/ContentContext';
import { formatDate, formatTime, formatDateTime } from '../utils/dateUtils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { generateSingleCroppedThumbnail } from '../utils/imageCropper';
import { getImageOrientation } from '../utils/productUtils';
import UploadSection from './UploadSection';
import SelectSection from './SelectSection';
import CartSection from './CartSection';
import ImageEditorModal from './ImageEditorModal';
import CheckoutSection from './CheckoutSection';
import OrdersSection from './OrdersSection';
import ProfileSection from './ProfileSection';
import AdminOrdersSection from './admin/AdminOrdersSection';
import ProductManagement from './admin/ProductManagement';
import CategoryManagement from './admin/CategoryManagement';
import ShippingManagement from './admin/ShippingManagement';
import CouponManagement from './admin/CouponManagement';
import TaxRateManagementSection from './admin/TaxRateManagementSection';
import UserManagement from './admin/UserManagement';
import ReportsSection from './admin/ReportsSection';
import SettingsSection from './admin/SettingsSection';
import RecycleBinSection from './admin/RecycleBinSection';
import LiveClock from './LiveClock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Dashboard() {
  const { user, setUser, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useContent();
  const [activeTab, setActiveTab] = useState(() => {
    // For customers: ALWAYS default to upload (don't persist tab)
    // For admins: Load from localStorage or default to orders
    try {
      // Customers always start on upload tab
      if (!user || user.role !== 'admin') {
        console.log('Customer: defaulting to upload tab');
        return 'upload';
      }
      
      // Admins can use saved tab or URL hash
      const savedTab = localStorage.getItem('printStudioActiveTab');
      const hash = window.location.hash.replace('#', '');
      const initialTab = savedTab || hash || 'orders';
      
      console.log('Admin: Initial activeTab:', initialTab);
      return initialTab;
    } catch (error) {
      console.error('Failed to load active tab from localStorage:', error);
      return 'upload';
    }
  });
  const [siteTimezone, setSiteTimezone] = useState('Australia/Sydney');  // Store site timezone
  const [uploadedImages, setUploadedImages] = useState(() => {
    // Initialize uploaded images from localStorage
    try {
      const savedImages = localStorage.getItem('printStudioImages');
      return savedImages ? JSON.parse(savedImages) : [];
    } catch (error) {
      console.error('Failed to load images from localStorage:', error);
      return [];
    }
  });
  const [cartItems, setCartItems] = useState(() => {
    // Initialize cart from localStorage
    try {
      const savedCart = localStorage.getItem('printStudioCart');
      const cartData = savedCart ? JSON.parse(savedCart) : [];
      console.log('🛒 Loading cart from localStorage:', cartData.length, 'items');
      return cartData;
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      return [];
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printSizes, setPrintSizes] = useState({});
  const [shippingOptions, setShippingOptions] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [editingImage, setEditingImage] = useState(null);
  // showModalSuccess removed - now using toast notifications instead
  const [modalAddedCount, setModalAddedCount] = useState(0);
  const [lastEditedImage, setLastEditedImage] = useState(null);
  const [testMode, setTestMode] = useState(() => {
    // Load test mode from localStorage
    return localStorage.getItem('adminTestMode') === 'true';
  });
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    site_title: 'Print Studio',
    site_logo: '',
    site_tagline: 'Professional Photo Printing'
  });
  
  // Removed duplicate dialog - now auto-renames with toast notification
  
  // Global paper settings for entire order
  // Paper and Border selection removed - now handled in Order Summary modal
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [orderSummaryData, setOrderSummaryData] = useState(null);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setActiveTab(hash);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab) {
      window.history.pushState(null, '', `#${activeTab}`);
    }
  }, [activeTab]);

  // Persist cart to localStorage whenever it changes (strip image data to save space)
  useEffect(() => {
    try {
      // Only store essential cart data - minimize size for localStorage limits
      const cartForStorage = cartItems.map(item => ({
        id: item.id, // Include ID for proper cart item identification
        filename: item.filename,
        image_id: item.image_id, // Reference to fetch image if needed
        // Don't store thumbnail and preview - too large, can be regenerated
        size: item.size,
        quantity: item.quantity,
        paper_type: item.paper_type,
        border: item.border,
        rotation: item.rotation,
        aspectOrientation: item.aspectOrientation,
        crop_settings: item.crop_settings,
        scale: item.scale,
        price: item.price
      }));
      
      const cartString = JSON.stringify(cartForStorage);
      const cartSize = new Blob([cartString]).size;
      
      console.log(`💾 Saving cart to localStorage: ${cartForStorage.length} items, ${(cartSize/1024).toFixed(2)}KB`);
      
      if (cartSize > 500000) {
        console.warn('⚠️ Cart is too large even without thumbnails. Consider reducing items.');
      }
      
      localStorage.setItem('printStudioCart', cartString);
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
      // If storage fails, silently continue - cart will just not persist
    }
  }, [cartItems]);

  // Save uploaded images to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('printStudioImages', JSON.stringify(uploadedImages));
    } catch (error) {
      console.error('Failed to save images to localStorage:', error);
      // If storage is full, try to clear old data
      if (error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing images');
        localStorage.removeItem('printStudioImages');
      }
    }
  }, [uploadedImages]);

  useEffect(() => {
    fetchSiteSettings();
    fetchPrintSizes();
    fetchShippingOptions();
    fetchOrders();
    
    // Clean up any old oversized localStorage data
    try {
      const savedCart = localStorage.getItem('printStudioCart');
      if (savedCart && savedCart.length > 500000) { // If larger than 500KB
        console.warn('Cart in localStorage is too large, clearing it');
        localStorage.removeItem('printStudioCart');
      }
    } catch (error) {
      console.error('Failed to check localStorage:', error);
    }
  }, []);
  
  // Note: Cart items now only store references (filename, image_id) to uploaded images
  // Thumbnails and image_data are looked up from uploadedImages when needed
  // This prevents the 413 error from storing large base64 strings in cart state

  // Clean up cart data on mount: remove corrupted thumbnails AND orphaned items
  useEffect(() => {
    if (cartItems.length === 0) return;
    
    let needsUpdate = false;
    let cleanedCart = [...cartItems];
    
    // Step 1: Remove corrupted thumbnail/image_data fields from all items
    const hasInvalidData = cartItems.some(item => {
      // Check if item has corrupted thumbnail data
      if (item.thumbnail && typeof item.thumbnail === 'string' && item.thumbnail.length < 100) {
        return true;
      }
      // Check for known corrupted patterns
      if (item.thumbnail && (item.thumbnail === '2Q==' || item.thumbnail === 'Z' || item.thumbnail === '9k=')) {
        return true;
      }
      // Check if item has large image_data stored (old format)
      if (item.image_data && typeof item.image_data === 'string' && item.image_data.length > 1000) {
        return true;
      }
      return false;
    });
    
    if (hasInvalidData) {
      console.warn('🧹 Cleaning up corrupted cart data from old format');
      cleanedCart = cartItems.map(item => ({
        id: item.id,
        filename: item.filename,
        image_id: item.image_id,
        size: item.size,
        quantity: item.quantity,
        paper_type: item.paper_type,
        border: item.border,
        rotation: item.rotation,
        scale: item.scale,
        aspectOrientation: item.aspectOrientation,
        crop_settings: item.crop_settings,
        price: item.price
        // Explicitly remove: thumbnail, image_data, preview
      }));
      needsUpdate = true;
    }
    
    // Step 2: Remove orphaned cart items (items without corresponding uploaded images)
    if (uploadedImages.length > 0) {
      const uploadedFilenames = uploadedImages.map(img => img.filename);
      const orphanedItems = cleanedCart.filter(item => !uploadedFilenames.includes(item.filename));
      
      if (orphanedItems.length > 0) {
        console.warn(`🧹 Removing ${orphanedItems.length} orphaned cart item(s):`, orphanedItems.map(i => i.filename));
        cleanedCart = cleanedCart.filter(item => uploadedFilenames.includes(item.filename));
        needsUpdate = true;
        toast.info(`Removed ${orphanedItems.length} item(s) from cart - source images not found`);
      }
    }
    
    if (needsUpdate) {
      setCartItems(cleanedCart);
      if (hasInvalidData && cleanedCart.length > 0) {
        toast.info('Cart data cleaned up');
      }
    }
  }, [uploadedImages]); // Run when uploadedImages loads

  useEffect(() => {
    // Ensure customers always land on upload tab when role changes
    if (user?.role && user.role !== 'admin' && activeTab !== 'upload') {
      console.log('Customer logged in: resetting to upload tab');
      setActiveTab('upload');
    }
  }, [user?.role]);

  // Save active tab to localStorage for admins only (customers always start on upload)
  useEffect(() => {
    try {
      if (user?.role === 'admin') {
        localStorage.setItem('printStudioActiveTab', activeTab);
        console.log('Saved admin active tab to localStorage:', activeTab);
      }
    } catch (error) {
      console.error('Failed to save active tab to localStorage:', error);
    }
  }, [activeTab, user?.role]);

  // Fetch site settings on mount to get timezone
  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get('/settings');
      // Convert relative URLs to full URLs
      const settingsData = {
        ...response.data,
        site_logo: response.data.site_logo ? `${BACKEND_URL}${response.data.site_logo}` : '',
        favicon: response.data.favicon ? `${BACKEND_URL}${response.data.favicon}` : ''
      };
      setSiteSettings(settingsData);
      // Update site timezone if provided
      if (settingsData.timezone) {
        setSiteTimezone(settingsData.timezone);
      }
      // Update document title
      document.title = settingsData.site_title || 'Print Studio';
      // Update favicon if provided
      if (settingsData.favicon) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = settingsData.favicon;
        document.head.appendChild(link);
      }
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
    }
  };

  const fetchPrintSizes = async () => {
    try {
      const response = await axios.get('/print/sizes');
      setPrintSizes(response.data);
      
      // Log all fetched ratios to verify admin configuration
      console.log('📦 Admin Print Sizes Loaded:');
      Object.entries(response.data).forEach(([key, config]) => {
        console.log(`  ${key}: ${config.display_name} → ratio ${config.crop_ratio_x}:${config.crop_ratio_y} (${config.crop_ratio_x}/${config.crop_ratio_y} = ${(config.crop_ratio_x/config.crop_ratio_y).toFixed(3)})`);
      });
    } catch (error) {
      console.error('Failed to fetch print sizes:', error);
    }
  };

  const fetchShippingOptions = async () => {
    try {
      const response = await axios.get('/shipping/options');
      setShippingOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch shipping options:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;

    // Fetch upload settings for validation
    let uploadSettings = null;
    try {
      const settingsResponse = await axios.get('/upload-settings');
      uploadSettings = settingsResponse.data;
    } catch (error) {
      console.error('Failed to fetch upload settings:', error);
      // Continue with default validation
    }

    // Validate files against settings
    const filesToUpload = Array.from(files);
    const invalidFiles = [];
    const validFiles = [];

    console.log('🔍 Upload settings:', uploadSettings);
    console.log('📁 Files to validate:', filesToUpload.map(f => ({name: f.name, size: f.size})));

    for (const file of filesToUpload) {
      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileSizeMB = file.size / (1024 * 1024);
      
      console.log(`🔍 Validating file: ${file.name}, ext: ${fileExt}, size: ${fileSizeMB.toFixed(2)}MB`);
      
      // Check file format
      if (uploadSettings && !uploadSettings.allowed_file_formats.includes(fileExt)) {
        invalidFiles.push({
          file,
          reason: `File format .${fileExt} not allowed. Allowed: ${uploadSettings.allowed_file_formats.join(', ')}`
        });
        continue;
      }

      // Check file size
      if (uploadSettings) {
        if (fileSizeMB < uploadSettings.min_file_size_mb) {
          invalidFiles.push({
            file,
            reason: `File too small (${fileSizeMB.toFixed(2)}MB). Minimum: ${uploadSettings.min_file_size_mb}MB`
          });
          continue;
        }
        if (fileSizeMB > uploadSettings.max_file_size_mb) {
          invalidFiles.push({
            file,
            reason: `File too large (${fileSizeMB.toFixed(2)}MB). Maximum: ${uploadSettings.max_file_size_mb}MB`
          });
          continue;
        }
      }

      validFiles.push(file);
    }

    // Show warnings for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({file, reason}) => {
        toast.error(`${file.name}: ${reason}`);
      });
      
      // If no valid files, return early
      if (validFiles.length === 0) {
        return;
      }
    }

    // Check total upload size
    if (uploadSettings) {
      const totalSizeMB = validFiles.reduce((sum, file) => sum + (file.size / (1024 * 1024)), 0);
      if (totalSizeMB > uploadSettings.max_total_upload_mb) {
        toast.error(`Total upload size (${totalSizeMB.toFixed(2)}MB) exceeds maximum (${uploadSettings.max_total_upload_mb}MB)`);
        return;
      }
    }

    // Check for duplicates BEFORE uploading
    let filesToUploadFiltered = validFiles;
    const existingFilenames = uploadedImages.map(img => img.filename);
    
    console.log('📤 Upload attempt:', {
      newFiles: filesToUploadFiltered.map(f => f.name),
      existingFiles: existingFilenames
    });
    
    const duplicates = filesToUploadFiltered.filter(file => existingFilenames.includes(file.name));
    const nonDuplicates = filesToUploadFiltered.filter(file => !existingFilenames.includes(file.name));
    
    console.log('🔍 Duplicate check:', {
      duplicates: duplicates.map(f => f.name),
      nonDuplicates: nonDuplicates.map(f => f.name)
    });

    if (duplicates.length > 0) {
      // Auto-rename duplicates and show toast notification
      const duplicateNames = duplicates.map(f => f.name).join(', ');
      toast.warning(`⚠️ Duplicate files detected: ${duplicateNames}. Automatically renaming...`, {
        duration: 4000,
      });
      
      // Rename duplicates by adding (1), (2), etc. to filename
      const renamedFiles = duplicates.map(file => {
        let newName = file.name;
        let counter = 1;
        const nameParts = file.name.split('.');
        const extension = nameParts.pop();
        const baseName = nameParts.join('.');
        
        while (existingFilenames.includes(newName)) {
          newName = `${baseName} (${counter}).${extension}`;
          counter++;
        }
        
        return new File([file], newName, { type: file.type });
      });
      
      // Combine non-duplicates with renamed files
      filesToUploadFiltered = [...nonDuplicates, ...renamedFiles];
    }

    // No duplicates, proceed with upload
    console.log('✅ No duplicates, uploading...');
    await uploadFilesToServer(filesToUploadFiltered);
  };
  
  const uploadFilesToServer = async (files) => {
    if (!files || files.length === 0) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 second timeout
      });

      setUploadedImages(prev => [...prev, ...response.data.images]);
      setUploadedCount(response.data.images.length);
      // Bulk order modal will appear automatically via UploadSection useEffect
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload images. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Duplicate handlers removed - now auto-renames inline

  const addToCart = async (image, options) => {
    // Use the smart add/update function to combine items with same specs
    await addOrUpdateCartItems(image, [options]);
    toast.success(`Added ${image.filename} (${options.size}) to cart!`);
  };

  const updateCartItem = (itemId, updates) => {
    setCartItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, ...updates } : item
    ));
    toast.success('Updated cart item!');
  };

  const addOrUpdateCartItems = async (image, itemsArray) => {
    console.log('💾 Generating cropped thumbnails for:', image.filename, 'items:', itemsArray);
    
    // Generate cropped thumbnails like we were doing before
    const itemsWithThumbnails = await Promise.all(
      itemsArray.map(async (item) => {
        if (item.crop_settings && (image.image_data || image.preview)) {
          try {
            const itemKey = item.sku || item.size;
            console.log(`🎨 Generating cropped thumbnail for ${image.filename} - ${itemKey}`);
            console.log(`   Item rotation: ${item.rotation}, zoom: ${item.zoom}, orientation: ${item.aspectOrientation}`);
            
            const originalImageData = image.image_data || image.preview;
            const sizeConfig = printSizes[itemKey];
            const croppedThumb = await generateSingleCroppedThumbnail(
              originalImageData,
              itemKey,
              { 
                crop_settings: item.crop_settings, 
                rotation: item.rotation || 0,
                zoom: item.zoom || 1,
                printRatio: {
                  ratioX: sizeConfig?.crop_ratio_x || 3,
                  ratioY: sizeConfig?.crop_ratio_y || 2,
                  orientation: item.aspectOrientation || 'landscape'
                }
              }
            );
            
            console.log(`✅ Generated cropped thumbnail for ${image.filename} - ${itemKey}`);
            
            const ratioId = sizeConfig 
              ? `${sizeConfig.crop_ratio_x}:${sizeConfig.crop_ratio_y}`
              : itemKey;
            const croppedThumbId = `${image.filename}_${ratioId}_${Date.now()}`;
            
            return { 
              ...item, 
              croppedThumbnailId: croppedThumbId, 
              croppedThumbnail: croppedThumb 
            };
          } catch (error) {
            console.error(`❌ Failed to generate cropped thumbnail for ${item.sku || item.size}:`, error);
            return item;
          }
        }
        return item;
      })
    );
    
    // Store cropped thumbnails in uploadedImages
    setUploadedImages(prevImages => {
      return prevImages.map(img => {
        if (img.filename === image.filename) {
          const croppedThumbnails = { ...(img.croppedThumbnails || {}) };
          
          itemsWithThumbnails.forEach(item => {
            if (item.croppedThumbnail) {
              const itemKey = item.sku || item.size;
              const sizeConfig = printSizes[itemKey];
              const ratioId = sizeConfig 
                ? `${sizeConfig.crop_ratio_x}:${sizeConfig.crop_ratio_y}`
                : itemKey;
              
              // Store by SKU for consistency
              croppedThumbnails[itemKey] = {
                id: item.croppedThumbnailId,
                data: item.croppedThumbnail,
                ratio: ratioId,
                rotation: item.rotation || 0,
                zoom: item.zoom || 1,
                aspectOrientation: item.aspectOrientation || 'landscape',
                timestamp: Date.now()
              };
              console.log(`💾 Stored cropped thumbnail for ${itemKey}`);
            }
          });
          
          return { ...img, croppedThumbnails };
        }
        return img;
      });
    });
    
    setCartItems(prev => {
      let updatedCart = [...prev];
      
      itemsArray.forEach(item => {
        // For cart edits, find by cart item ID directly
        // Use SKU for matching if available, fall back to size for backward compatibility
        const itemKey = item.sku || item.size;
        const existingItemIndex = item.isCartEdit && item.cartItemId
          ? updatedCart.findIndex(cartItem => cartItem.id === item.cartItemId)
          : updatedCart.findIndex(cartItem =>
              cartItem.filename === image.filename &&
              (cartItem.sku === itemKey || cartItem.size === itemKey) &&
              cartItem.paper_type === item.paper_type &&
              Boolean(cartItem.border) === Boolean(item.border)
            );

        if (existingItemIndex !== -1) {
          // If forceUpdate flag is set, always update crop settings
          if (item.forceUpdate) {
            // forceUpdate: update crop settings and optionally update quantity
            const newQuantity = item.quantity > 0 ? item.quantity : updatedCart[existingItemIndex].quantity;
            updatedCart[existingItemIndex] = {
              ...updatedCart[existingItemIndex],
              sku: item.sku || updatedCart[existingItemIndex].sku,
              quantity: newQuantity,
              rotation: item.rotation !== undefined ? item.rotation : updatedCart[existingItemIndex].rotation,
              zoom: item.zoom !== undefined ? item.zoom : updatedCart[existingItemIndex].zoom,
              scale: item.scale !== undefined ? item.scale : updatedCart[existingItemIndex].scale,
              crop_settings: item.crop_settings || updatedCart[existingItemIndex].crop_settings,
              cropPosition: item.cropPosition || updatedCart[existingItemIndex].cropPosition,
              aspectOrientation: item.aspectOrientation || updatedCart[existingItemIndex].aspectOrientation
            };
            console.log(`🔄 Force updated cart item: ${image.filename} - ${item.sku || item.size}, zoom: ${updatedCart[existingItemIndex].zoom}, cropPosition:`, updatedCart[existingItemIndex].cropPosition);
          } else if (item.isCartEdit) {
            // For cart edits, REPLACE the quantity instead of adding
            const newQuantity = item.quantity;
            
            if (newQuantity > 0) {
              // Update with new quantity (REPLACE not ADD)
              updatedCart[existingItemIndex] = {
                ...updatedCart[existingItemIndex],
                quantity: newQuantity,
                rotation: item.rotation !== undefined ? item.rotation : updatedCart[existingItemIndex].rotation,
                zoom: item.zoom !== undefined ? item.zoom : updatedCart[existingItemIndex].zoom,
                scale: item.scale !== undefined ? item.scale : updatedCart[existingItemIndex].scale,
                crop_settings: item.crop_settings || updatedCart[existingItemIndex].crop_settings,
                cropPosition: item.cropPosition || updatedCart[existingItemIndex].cropPosition,
                aspectOrientation: item.aspectOrientation || updatedCart[existingItemIndex].aspectOrientation
              };
              console.log(`✏️ Cart edit: ${image.filename} - ${item.sku || item.size}, zoom: ${updatedCart[existingItemIndex].zoom}, cropPosition:`, updatedCart[existingItemIndex].cropPosition);
            } else {
              // Remove item if quantity is 0
              updatedCart.splice(existingItemIndex, 1);
            }
          } else {
            // For new additions, ADD the quantity difference
            const newQuantity = updatedCart[existingItemIndex].quantity + item.quantity;
            
            if (newQuantity > 0) {
              // Update with new quantity
              updatedCart[existingItemIndex] = {
                ...updatedCart[existingItemIndex],
                quantity: newQuantity,
                rotation: item.rotation !== undefined ? item.rotation : updatedCart[existingItemIndex].rotation,
                zoom: item.zoom !== undefined ? item.zoom : updatedCart[existingItemIndex].zoom,
                scale: item.scale !== undefined ? item.scale : updatedCart[existingItemIndex].scale,
                crop_settings: item.crop_settings || updatedCart[existingItemIndex].crop_settings,
                cropPosition: item.cropPosition || updatedCart[existingItemIndex].cropPosition,
                aspectOrientation: item.aspectOrientation || updatedCart[existingItemIndex].aspectOrientation
              };
            } else {
              // Remove item if resulting quantity is 0 or negative
              updatedCart.splice(existingItemIndex, 1);
            }
          }
        } else if (item.quantity > 0) {
          // Add new item (only if quantity is positive)
          // NOTE: Don't store thumbnail/image_data in cart - these will be looked up from uploadedImages
          const newItem = {
            id: `${image.filename}-${Date.now()}-${item.sku}-${Math.random()}`,
            filename: image.filename,
            image_id: image.id, // Store reference instead of full data
            sku: item.sku,  // Use SKU as the primary key
            size: item.size,  // Keep size for display purposes
            paper_type: item.paper_type,
            border: item.border,
            quantity: item.quantity,
            crop_settings: item.crop_settings,
            cropPosition: item.cropPosition || { x: 0, y: 0 },  // Save pan position for restoration
            rotation: item.rotation || 0,
            zoom: item.zoom || 1,
            scale: item.scale || 100,
            aspectOrientation: item.aspectOrientation || 'landscape'
          };
          console.log(`➕ New cart item: ${image.filename} - ${item.sku}, zoom: ${newItem.zoom}, cropPosition:`, newItem.cropPosition);
          updatedCart.push(newItem);
        }
      });

      return updatedCart;
    });
  };

  const removeFromCart = (itemId) => {
    console.log('🗑️ Removing item with ID:', itemId);
    setCartItems(prev => {
      console.log('🗑️ Current cart items:', prev.length);
      console.log('🗑️ Cart item IDs:', prev.map(item => item.id));
      const filtered = prev.filter(item => item.id !== itemId);
      console.log('🗑️ After filter:', filtered.length);
      return filtered;
    });
    toast.success('Removed from cart');
  };


  const handleApplyBulkSettings = async (quantity, sku, imagesToApply = null) => {
    // Apply bulk quantity and size to specified images (or all if not specified)
    // Note: Paper type and border options will be selected in Order Summary modal during checkout
    const targetImages = imagesToApply || uploadedImages;
    
    // Get saved crop data from localStorage
    const savedCropData = JSON.parse(localStorage.getItem('tempCropData') || '{}');
    
    // Get product info from printSizes using SKU
    const product = printSizes[sku];
    const displaySize = product?.size || sku;  // Display size like "6x4"
    
    // Process images with orientation detection
    const bulkItems = await Promise.all(targetImages.map(async (image) => {
      // Always use filename as the key for consistency
      const imageKey = image.filename;
      const imageCrops = savedCropData[imageKey] || {};
      
      console.log(`🔍 Bulk: Looking up crops for ${imageKey}:`, Object.keys(imageCrops));
      
      // Default orientation based on IMAGE dimensions, not product
      const imageData = image.image_data || image.preview;
      let aspectOrientation = 'landscape';
      let zoom = 1;
      
      const portraitKey = `${sku}-portrait`;
      const landscapeKey = `${sku}-landscape`;
      
      // Check if user has saved a specific crop for this product (user choice takes priority)
      if (imageCrops[portraitKey]) {
        const saved = imageCrops[portraitKey];
        aspectOrientation = 'portrait';
        zoom = saved.zoom || 1;
        console.log(`✅ Bulk: Found portrait crop for ${sku}: zoom=${zoom}`);
      } else if (imageCrops[landscapeKey]) {
        const saved = imageCrops[landscapeKey];
        aspectOrientation = 'landscape';
        zoom = saved.zoom || 1;
        console.log(`✅ Bulk: Found landscape crop for ${sku}: zoom=${zoom}`);
      } else if (imageCrops[sku]) {
        const saved = imageCrops[sku];
        aspectOrientation = saved.aspectOrientation || 'landscape';
        zoom = saved.zoom || 1;
      } else if (imageData) {
        // No saved crop - detect orientation from image dimensions
        aspectOrientation = await getImageOrientation(imageData);
        console.log(`📐 Bulk: Auto-detected orientation for ${imageKey}: ${aspectOrientation}`);
      } else {
        console.log(`⚠️ Bulk: No saved crop or image data for ${imageKey} - ${sku}, defaulting to landscape`);
      }
      
      const cropSettings = { x: 0, y: 0, width: 100, height: 100 };
      
      console.log(`📦 Bulk Settings: ${image.filename} - sku: ${sku}, size: ${displaySize}, orientation: ${aspectOrientation}, zoom: ${zoom}x`);
      
      return {
        image_data: image.image_data,
        filename: image.filename,
        thumbnail: image.thumbnail,
        items: [{
          sku: sku,  // SKU is the primary key
          size: displaySize,  // Display size for UI
          quantity: parseInt(quantity) || 1,
          zoom: zoom,
          aspectOrientation: aspectOrientation,
          crop_settings: cropSettings
        }]
      };
    }));

    // Add all items to cart
    await Promise.all(bulkItems.map(item => addOrUpdateCartItems(item, item.items)));

    toast.success(`✅ Added ${targetImages.length} images to cart with ${quantity} copies each!`);
  };


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              {siteSettings.site_logo && (
                <img 
                  src={siteSettings.site_logo} 
                  alt="Logo" 
                  className="h-10 w-auto object-contain"
                />
              )}
              <h1 className="text-2xl font-bold text-gray-900">{siteSettings.site_title || 'Print Studio'}</h1>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Conditionally show Orders and Cart for non-admin users */}
              {user?.role !== 'admin' && (
                <div className="flex items-center gap-4 mr-4">
                  <button 
                    onClick={() => setActiveTab('orders')}
                    className="text-sm font-medium text-gray-700 hover:text-custom-orange hover:underline cursor-pointer flex items-center gap-1"
                  >
                    📋 Orders ({orders.length})
                  </button>
                  <button 
                    onClick={() => setActiveTab('cart')}
                    className="text-sm font-medium text-gray-700 hover:text-custom-orange hover:underline cursor-pointer flex items-center gap-2 relative"
                  >
                    🛒 Cart
                    {cartItems.length > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {cartItems.length}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {user?.role === 'admin' && (
                <>
                  <Badge variant="default" className="bg-green-100 text-green-800">
                    👑 Admin
                  </Badge>
                  
                  {/* Live Clock showing site timezone */}
                  <LiveClock 
                    timezone={siteTimezone} 
                    className="border border-gray-200 rounded-md px-3 py-1.5 bg-blue-50"
                  />
                  
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => setShowRecycleBin(true)}
                    className="flex items-center gap-2"
                    data-testid="recycle-bin-button"
                  >
                    🗑️ Recycle Bin
                  </Button>
                  
                  <Button
                    size="sm"
                    variant={testMode ? "default" : "outline"}
                    onClick={() => {
                      const newMode = !testMode;
                      setTestMode(newMode);
                      localStorage.setItem('adminTestMode', newMode.toString());
                      toast.success(newMode ? '🧪 Test Mode Enabled' : '✅ Test Mode Disabled');
                    }}
                    className={testMode ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-300" : ""}
                  >
                    {testMode ? '🧪 Test Mode ON' : '🧪 Test Mode'}
                  </Button>
                </>
              )}
              
              <div className="flex items-center gap-3">
                {/* Dark Mode Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="flex items-center gap-2"
                  title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {theme === 'dark' ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                </Button>
                
                {user?.picture && (
                  <img 
                    src={user.picture} 
                    alt={`${user.name} profile picture`}
                    className="w-8 h-8 rounded-full"
                  />
                )}
                
                <button 
                  onClick={() => setActiveTab('profile')}
                  className="text-sm font-medium text-gray-700 hover:text-custom-orange hover:underline cursor-pointer"
                >
                  {user?.name || 'User'}
                </button>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={logout}
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full ${user?.role === 'admin' ? 'grid-cols-10' : 'grid-cols-5'} mb-8`}>
            {user?.role === 'admin' ? (
              // Admin-only tabs
              <>
                <TabsTrigger value="orders" data-testid="orders-tab">
                  📋 Orders
                </TabsTrigger>
                <TabsTrigger value="products" data-testid="products-tab">
                  📦 Products
                </TabsTrigger>
                <TabsTrigger value="categories" data-testid="categories-tab">
                  🏷️ Categories
                </TabsTrigger>
                <TabsTrigger value="shipping" data-testid="shipping-tab">
                  🚚 Shipping
                </TabsTrigger>
                <TabsTrigger value="tax" data-testid="tax-tab">
                  💰 Tax Rates
                </TabsTrigger>
                <TabsTrigger value="coupons" data-testid="coupons-tab">
                  🎫 Coupons
                </TabsTrigger>
                <TabsTrigger value="users" data-testid="users-tab">
                  👥 Users
                </TabsTrigger>
                <TabsTrigger value="reports" data-testid="reports-tab">
                  📊 Reports
                </TabsTrigger>
                <TabsTrigger value="settings" data-testid="settings-tab">
                  ⚙️ Settings
                </TabsTrigger>
              </>
            ) : (
              // Customer-only tabs - Upload → Edit → Cart → Checkout → Orders
              <>
                <TabsTrigger value="upload" data-testid="upload-tab">
                  Upload Images
                </TabsTrigger>
                
                {/* Edit Images tab */}
                {uploadedImages.length > 0 && (
                  <TabsTrigger value="select" data-testid="select-tab">
                    Edit Images ({uploadedImages.length})
                  </TabsTrigger>
                )}
                
                {/* Cart tab */}
                <TabsTrigger value="cart" data-testid="cart-tab">
                  Cart ({cartItems.length})
                </TabsTrigger>
                
                {/* Checkout tab - only show if cart has items */}
                {cartItems.length > 0 && (
                  <TabsTrigger value="checkout" data-testid="checkout-tab">
                    Checkout
                  </TabsTrigger>
                )}
                
                {/* Orders tab */}
                <TabsTrigger value="orders" data-testid="orders-tab">
                  Orders
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {user?.role === 'admin' ? (
            // Admin content sections
            <>
              <TabsContent value="orders" className="space-y-6">
                <AdminOrdersSection 
                  orders={orders} 
                  testMode={testMode} 
                  onOrderDeleted={fetchOrders}
                  siteTimezone={siteTimezone}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  formatDateTime={formatDateTime}
                />
              </TabsContent>
              
              <TabsContent value="products" className="space-y-6">
                <ProductManagement printSizes={printSizes} onUpdateProducts={fetchPrintSizes} />
              </TabsContent>
              
              <TabsContent value="categories" className="space-y-6">
                <CategoryManagement />
              </TabsContent>
              
              <TabsContent value="shipping" className="space-y-6">
                <ShippingManagement shippingOptions={shippingOptions} onUpdateShipping={fetchShippingOptions} />
              </TabsContent>
              
              <TabsContent value="tax" className="space-y-6">
                <TaxRateManagementSection />
              </TabsContent>
              
              <TabsContent value="coupons" className="space-y-6">
                <CouponManagement 
                  siteTimezone={siteTimezone}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  formatDateTime={formatDateTime}
                />
              </TabsContent>
              
              <TabsContent value="users" className="space-y-6">
                <UserManagement 
                  siteTimezone={siteTimezone}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  formatDateTime={formatDateTime}
                />
              </TabsContent>


              <TabsContent value="reports" className="space-y-6">
                <ReportsSection />
              </TabsContent>

              <TabsContent value="settings" className="space-y-6">
                <SettingsSection onSettingsUpdate={fetchSiteSettings} />
              </TabsContent>

              <TabsContent value="profile" className="space-y-6">
                <ProfileSection user={user} onUserUpdate={setUser} />
              </TabsContent>
            </>
          ) : (
            // Customer content sections (Upload → Edit → Cart → Orders)
            <>
              <TabsContent value="upload" className="space-y-6">
                <UploadSection 
                  onUpload={onUpload} 
                  loading={loading}
                  uploadedCount={uploadedImages.length}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onApplyBulkSettings={handleApplyBulkSettings}
                  printSizes={printSizes}
                  cartItems={cartItems}
                  uploadedImages={uploadedImages}
                />
              </TabsContent>

              <TabsContent value="select" className="space-y-6">
                <SelectSection 
                  images={uploadedImages}
                  printSizes={printSizes}
                  onAddToCart={addToCart}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  cartItems={cartItems}
                  setImages={setUploadedImages}
                  onEditImage={setEditingImage}
                />
              </TabsContent>

              <TabsContent value="cart" className="space-y-6">
                <CartSection 
                  items={cartItems}
                  printSizes={printSizes}
                  shippingOptions={shippingOptions}
                  onRemoveItem={removeFromCart}
                  onOrderComplete={fetchOrders}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  onUpdateItem={updateCartItem}
                  onUpdateQuantity={(itemId, newQuantity) => updateCartItem(itemId, { quantity: newQuantity })}
                  uploadedImages={uploadedImages}
                  onShowOrderSummary={() => setShowOrderSummary(true)}
                  orderSummaryData={orderSummaryData}
                  setEditingImage={setEditingImage}
                />
              </TabsContent>

              <TabsContent value="checkout" className="space-y-6">
                <CheckoutSection 
                  items={cartItems}
                  printSizes={printSizes}
                  shippingOptions={shippingOptions}
                  onOrderComplete={fetchOrders}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  uploadedImages={uploadedImages}
                  user={user}
                  onUserUpdate={setUser}
                />
              </TabsContent>

              <TabsContent value="orders" className="space-y-6">
                <OrdersSection 
                  orders={orders}
                  siteTimezone={siteTimezone}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  formatDateTime={formatDateTime}
                  setActiveTab={setActiveTab}
                  printSizes={printSizes}
                />
              </TabsContent>

              <TabsContent value="profile" className="space-y-6">
                <ProfileSection user={user} onUserUpdate={setUser} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>

      {/* Duplicate dialog removed - now shows toast notification */}

      {/* Recycle Bin Dialog */}
      <Dialog open={showRecycleBin} onOpenChange={setShowRecycleBin}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('dialog.recycle_bin', 'Recycle Bin')}</DialogTitle>
            <DialogDescription>
              {t('dialog.recycle_bin_desc', 'View and restore deleted items or permanently delete them')}
            </DialogDescription>
          </DialogHeader>
          <RecycleBinSection 
            siteTimezone={siteTimezone}
            formatDate={formatDate}
            formatTime={formatTime}
            formatDateTime={formatDateTime}
          />
        </DialogContent>
      </Dialog>

      {/* Image Editor Modal */}
      {editingImage && (
        <ImageEditorModal
          data={editingImage}
          printSizes={printSizes}
          cartItems={cartItems}
          allImages={uploadedImages} // Pass all images for navigation
          setActiveTab={setActiveTab} // Pass setActiveTab for "Go to Cart" button
          onDeleteImage={(imageId) => {
            // Remove image from uploaded images
            setUploadedImages(prev => prev.filter(img => 
              img.id !== imageId && img.filename !== imageId
            ));
            // Also remove from cart if it exists
            setCartItems(prev => prev.filter(item => 
              item.filename !== imageId && item.id !== imageId
            ));
            // Close the modal
            setEditingImage(null);
            toast.success('Image deleted successfully');
          }}
          onClose={() => setEditingImage(null)}
          onUpdateThumbnail={(filename, size, thumbnailData) => {
            // Update the uploaded images with the new cropped thumbnail in real-time
            setUploadedImages(prevImages => {
              return prevImages.map(img => {
                if (img.filename === filename) {
                  const croppedThumbnails = { ...(img.croppedThumbnails || {}) };
                  croppedThumbnails[size] = thumbnailData;
                  return { ...img, croppedThumbnails };
                }
                return img;
              });
            });
            console.log(`🔄 Real-time thumbnail update for ${filename} - ${size}`);
          }}
          onSave={async (updates) => {
            // Update or add items to cart (could be multiple sizes)
            
            // Determine which image data to use
            const imageData = editingImage.isCartEdit 
              ? editingImage.cartItem  // For cart edits, use the cart item
              : editingImage.image;    // For new additions, use the uploaded image
            
            let addedCount = 0;
            if (updates.items) {
              // Multiple items - use smart add/update function
              await addOrUpdateCartItems(imageData, updates.items);
              addedCount = updates.totalQuantity || 0;
              setModalAddedCount(addedCount);
            } else {
              // Single item (shouldn't happen but handle it)
              await addOrUpdateCartItems(imageData, [updates]);
              addedCount = updates.quantity || 1;
              setModalAddedCount(addedCount);
            }
            // Store the editing data for reopening
            setLastEditedImage(editingImage);
            // DON'T close modal - let user continue to next image
            // setEditingImage(null);
            // Show success dialog
            // setShowModalSuccess(true);
            
            // Show success message
            toast.success(`${editingImage.isCartEdit ? 'Updated' : 'Added'} ${addedCount} item(s) ${editingImage.isCartEdit ? 'in' : 'to'} cart! Continue to next image or close when done.`);
          }}
        />
      )}

    </div>
  );
}

// Sort Products Modal Component
// OLD Product Attributes Modal - Removed and replaced with ProductAttributesModalBulk

// Coupon Management Section Component  

export default Dashboard;
